"use client"

import { useEffect } from "react"
import Link from "next/link"
import { Server, AlertTriangle, ArrowLeft, Terminal, Search } from "lucide-react"
import { Button } from "@/components/ui/button"

export default function TemplateInstallerPage() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-[#1a1c1e] text-white">
      <header className="w-full border-b border-[#2c2e30] bg-[#1a1c1e]/95 backdrop-blur supports-[backdrop-filter]:bg-[#1a1c1e]/60">
        <div className="container flex h-16 items-center space-x-4 sm:justify-between sm:space-x-0">
          <div className="flex gap-6 md:gap-10">
            <Link href="/" className="flex items-center space-x-2">
              <Server className="h-6 w-6 text-[#e57312]" />
              <span className="inline-block font-bold text-[#e57312]">HSVE</span>
            </Link>
            <nav className="hidden gap-6 md:flex">
              <Link
                href="/"
                className="flex items-center text-sm font-medium text-gray-300 transition-colors hover:text-white"
              >
                Home
              </Link>
              <Link
                href="/about"
                className="flex items-center text-sm font-medium text-gray-300 transition-colors hover:text-white"
              >
                About
              </Link>
              <div className="relative group">
                <button className="flex items-center text-sm font-medium text-gray-300 transition-colors hover:text-white">
                  Services
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="ml-1 h-3 w-3"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
                <div className="absolute left-0 top-full z-50 mt-2 hidden w-48 rounded-md bg-[#252729] p-2 shadow-lg group-hover:block group-focus-within:block">
                  <Link
                    href="/templates"
                    className="block rounded-md px-3 py-2 text-sm text-gray-300 hover:bg-[#1a1c1e] hover:text-white"
                  >
                    Proxmox Templates Script
                  </Link>
                </div>
              </div>
              <Link
                href="/contact"
                className="flex items-center text-sm font-medium text-gray-300 transition-colors hover:text-white"
              >
                Contact
              </Link>
            </nav>
          </div>
          <div className="flex flex-1 items-center justify-end space-x-4">
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:flex border-[#e57312] text-[#e57312] hover:bg-[#e57312]/10"
            >
              Log in
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-3xl w-full">
          <div className="bg-[#252729] rounded-xl border border-[#2c2e30] overflow-hidden">
            <div className="p-6 md:p-8">
              <div className="flex flex-col items-center text-center mb-8">
                <div className="relative w-24 h-24 mb-6">
                  <div className="absolute inset-0 bg-[#e57312]/20 rounded-full animate-ping"></div>
                  <div className="relative flex items-center justify-center w-24 h-24 bg-[#e57312]/10 rounded-full border-2 border-[#e57312]">
                    <AlertTriangle className="h-12 w-12 text-[#e57312]" />
                  </div>
                </div>
                <h1 className="text-3xl font-bold mb-2">Nice try, sysadmin!</h1>
                <p className="text-gray-400 max-w-lg">
                  The template installer script is only available to our registered clients through our secure portal.
                </p>
              </div>

              <div className="bg-black rounded-lg p-4 font-mono text-sm mb-8">
                <div className="flex items-start">
                  <Terminal className="h-5 w-5 text-[#e57312] mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-green-500">root@pve:~#</div>
                    <div className="text-gray-300">wget https://hsve.cc/scripts/templateinstaller</div>
                    <div className="text-red-400 mt-1">
                      ERROR: Access denied. Authentication required.
                      <br />
                      Please log in to your HSVE client portal to access this resource.
                    </div>
                    <div className="text-green-500 mt-2">root@pve:~#</div>
                    <div className="text-gray-300">_</div>
                  </div>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-[#1a1c1e] rounded-lg border border-[#2c2e30] p-4">
                  <h3 className="font-bold text-lg mb-2 flex items-center">
                    <Search className="h-5 w-5 text-[#e57312] mr-2" />
                    Looking for templates?
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Check out our templates page to learn more about our Proxmox VM templates and how they can help
                    streamline your deployment process.
                  </p>
                  <Link href="/templates">
                    <Button className="w-full bg-[#e57312] hover:bg-[#e57312]/90 text-white">View Templates</Button>
                  </Link>
                </div>

                <div className="bg-[#1a1c1e] rounded-lg border border-[#2c2e30] p-4">
                  <h3 className="font-bold text-lg mb-2 flex items-center">
                    <Server className="h-5 w-5 text-[#e57312] mr-2" />
                    Need access?
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Contact our sales team to learn more about our template services and how to get access to our
                    premium Proxmox templates.
                  </p>
                  <Button variant="outline" className="w-full border-[#e57312] text-[#e57312] hover:bg-[#e57312]/10">
                    Contact Sales
                  </Button>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link href="/" className="inline-flex items-center text-[#e57312] hover:underline">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Return to Homepage
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Harvey's Virtual Environment. All rights reserved.
          </div>
        </div>
      </main>
    </div>
  )
}
